import React, { useEffect, useRef, useState } from 'react';
import { familleCompte } from '../../lib/modulesCatalog';
import { Receipt, CalendarClock, PartyPopper, Loader2, User, Users as UsersIcon, CalendarCheck, FileText, Wallet } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';
import { SuiviPrivatisationNotificationBlocks, DevisNotifications } from './SuiviPrivatisationNotificationBlocks';
import { OrganisateurNotificationBlocks, OrganisateurNotifications, ORGANISATEUR_NOTIFS_VIDES } from './OrganisateurNotificationBlocks';
import { DemandeDevisNotificationBlocks, DemandeDevisNotifications, DEMANDE_DEVIS_NOTIFS_VIDES } from './DemandeDevisNotificationBlocks';
import { ModeNotificationsContext, FournisseurEnregistrement, EtatEnregistrement, GroupeNotifications } from '../ui/LigneNotification';

// Jetons propres au bloc "RDV Pro" (pas dans ALL_CLIENT_TOKENS ci-dessous — le serveur ne calcule ces
// variables que pour cet email précis, les ajouter au jeu commun laisserait un "{{...}}" brut si inséré
// par erreur dans un autre bloc comme Facture/Congés/Événementiel).
const RDV_PRO_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{lien_annulation}}', label: 'Bouton "Annuler le RDV" (si autorisé sur la démarche)' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons propres au bloc "RDV Pro annulé par le client" — reçu par l'ÉTABLISSEMENT (pas le client),
// {{prenom}}/{{nom}} désignent ici le contact qui a annulé, {{motif}} le texte qu'il a saisi.
const PRO_RDV_CANCELLED_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{motif}}', label: "Motif de l'annulation" },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons propres au bloc "Démarches client" (paiement confirmé) — même principe, calculés uniquement
// pour cet email précis.
const DOC_PAYMENT_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{articles}}', label: 'Choix commandés' },
    { token: '{{montant}}', label: 'Montant réglé' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons propres au bloc "Assignation RDV Pro" (salarié) — {{prenom}}/{{nom}} désignent ici le salarié
// assigné, pas le contact (contrairement aux autres blocs RDV Pro côté client).
const PRO_RDV_ASSIGNMENT_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du salarié' },
    { token: '{{nom}}', label: 'Nom du salarié' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{client_nom}}', label: 'Nom du client/contact' },
    { token: '{{raison}}', label: 'Raison du rendez-vous' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons propres aux blocs "Notes de frais" — {{prenom}}/{{nom}} désignent le salarié qui a soumis la
// note de frais, calculés uniquement pour ces deux emails précis.
const EXPENSE_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du salarié' },
    { token: '{{nom}}', label: 'Nom du salarié' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{categorie}}', label: 'Catégorie de la dépense' },
    { token: '{{montant}}', label: 'Montant' },
    { token: '{{motif}}', label: 'Motif du refus (si refusée)' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jeton propre au bloc "Mode kiosque" (code PIN) — {{prenom}}/{{nom}} désignent le salarié qui reçoit
// son code, calculés uniquement pour cet email précis.
const KIOSK_PIN_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du salarié' },
    { token: '{{nom}}', label: 'Nom du salarié' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{code_pin}}', label: 'Code PIN à 4 chiffres' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Toutes les variables des 4 emails réunies, disponibles dans chaque bloc (même principe que côté
// Prestation : le serveur calcule toujours l'intégralité du jeu de variables, celles qui ne concernent
// pas l'email en cours restent simplement vides — jamais de "{{...}}" brut laissé dans l'email envoyé).
const ALL_CLIENT_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{entreprise_telephone}}', label: 'Numéro entreprise' },
    { token: '{{entreprise_email}}', label: 'Email entreprise' },
    { token: '{{entreprise_adresse}}', label: 'Adresse entreprise' },
    { token: '{{entreprise_tva}}', label: 'TVA entreprise' },
    { token: '{{entreprise_siret}}', label: 'SIRET entreprise' },
    { token: '{{client_nom}}', label: 'Nom client' },
    { token: '{{date_facture}}', label: 'Date de la facture' },
    { token: '{{montant}}', label: 'Montant total TTC' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer (0 si soldée)' },
    { token: '{{lien_paiement}}', label: 'Lien de paiement Stripe (si configuré)' },
    { token: '{{salarie_prenom}}', label: 'Prénom salarié' },
    { token: '{{salarie_nom}}', label: 'Nom salarié' },
    { token: '{{type_absence}}', label: "Type d'absence" },
    { token: '{{date_debut}}', label: 'Date de début' },
    { token: '{{date_fin}}', label: 'Date de fin' },
    { token: '{{jours_accordes}}', label: 'Jours accordés' },
    { token: '{{note}}', label: 'Note (si renseignée)' },
    { token: '{{signataire}}', label: 'Signataire' },
    { token: '{{mois}}', label: 'Mois du planning' },
    { token: '{{annee}}', label: 'Année du planning' },
    { token: '{{logo}}', label: 'Votre logo' },
];

/** Sections "Facture" (Client) / "Demande de congé(s)" + "Événementiel" (Salarié) de l'onglet
 *  Notifications de la page Paramètres centrale — chaque section n'apparaît que si le module
 *  correspondant est actif, pour les deux types de client (Restauration et Prestation). Table à part
 *  (client_notification_settings) car partagée entre les deux types de client et plusieurs modules
 *  indépendants, séparée de prestation_notification_settings (100% Prestation). */
export const ClientEmailNotificationsSettings: React.FC<{
    activeModules: string[];
    clientType?: string | null;
    /** Destinataire imposé par la page Paramètres (un seul filtre pour tout l'onglet). */
    destinataire?: 'client' | 'salarie' | 'etablissement';
}> = ({ activeModules, clientType, destinataire }) => {
    const hasModule = (name: string) => activeModules.includes(name);
    const showFacture = hasModule('facture');
    const showConges = hasModule('demande_conges');
    const showEvenementiel = hasModule('evenementiel');
    const showProRdv = hasModule('rdv_pro') && familleCompte(clientType) === 'restauration';
    const showDocPayment = hasModule('crm');
    const showSuiviPrivatisation = hasModule('suivi_privatisation');
    const showOrganisateur = hasModule('menus_convives');
    // Les deux courriels de la demande de devis venue du simulateur : ils n'ont de sens que la ou le
    // Suivi de prestation existe, puisque c'est lui qui recoit la demande.
    const showDemandeDevis = hasModule('suivi_prestation');
    const showProRdvAssignment = hasModule('rdv_pro') && familleCompte(clientType) === 'restauration';
    const showExpense = hasModule('portail_salarie');
    const showKioskPin = hasModule('portail_salarie') && familleCompte(clientType) === 'restauration';
    const hasClientContent = showFacture || showProRdv || showDocPayment || showSuiviPrivatisation || showDemandeDevis || showOrganisateur;
    const hasSalarieContent = showConges || showEvenementiel || showProRdvAssignment || showExpense || showKioskPin;

    const [recipientLocal, setRecipient] = useState<'client' | 'salarie'>(hasClientContent ? 'client' : 'salarie');
    const recipient = destinataire ?? recipientLocal;
    const { enLignes } = React.useContext(ModeNotificationsContext);
    const [echecEnregistrement, setEchecEnregistrement] = useState(false);

    const [notifyFactureSend, setNotifyFactureSend] = useState(true);
    // Trois modèles n'avaient aucun interrupteur à l'écran. Les deux RDV avaient pourtant déjà leur
    // colonne en base, respectée à l'envoi — il manquait juste la case, et son écriture côté serveur.
    const [notifyFactureSendPaid, setNotifyFactureSendPaid] = useState(true);
    const [notifyProRdvReminder, setNotifyProRdvReminder] = useState(true);
    const [notifyProRdvReschedule, setNotifyProRdvReschedule] = useState(true);
    const [factureSendSubject, setFactureSendSubject] = useState('');
    const [factureSendBody, setFactureSendBody] = useState('');
    const [factureSendPaidSubject, setFactureSendPaidSubject] = useState('');
    const [factureSendPaidBody, setFactureSendPaidBody] = useState('');
    const [notifyCongesAccepted, setNotifyCongesAccepted] = useState(true);
    const [congesAcceptedSubject, setCongesAcceptedSubject] = useState('');
    const [congesAcceptedBody, setCongesAcceptedBody] = useState('');
    const [notifyCongesRefused, setNotifyCongesRefused] = useState(true);
    const [congesRefusedSubject, setCongesRefusedSubject] = useState('');
    const [congesRefusedBody, setCongesRefusedBody] = useState('');
    const [notifyEvenementielUpdate, setNotifyEvenementielUpdate] = useState(true);
    const [evenementielUpdateSubject, setEvenementielUpdateSubject] = useState('');
    const [evenementielUpdateBody, setEvenementielUpdateBody] = useState('');
    const [notifyProRdvConfirmation, setNotifyProRdvConfirmation] = useState(true);
    const [proRdvConfirmationSubject, setProRdvConfirmationSubject] = useState('');
    const [proRdvConfirmationBody, setProRdvConfirmationBody] = useState('');
    const [notifyDocPaymentConfirmed, setNotifyDocPaymentConfirmed] = useState(true);
    const [docPaymentConfirmedSubject, setDocPaymentConfirmedSubject] = useState('');
    const [docPaymentConfirmedBody, setDocPaymentConfirmedBody] = useState('');
    const [proRdvReminderSubject, setProRdvReminderSubject] = useState('');
    const [proRdvReminderBody, setProRdvReminderBody] = useState('');
    const [proRdvRescheduleSubject, setProRdvRescheduleSubject] = useState('');
    const [proRdvRescheduleBody, setProRdvRescheduleBody] = useState('');
    const [notifyProRdvCancelledByClient, setNotifyProRdvCancelledByClient] = useState(true);
    const [proRdvCancelledByClientSubject, setProRdvCancelledByClientSubject] = useState('');
    const [proRdvCancelledByClientBody, setProRdvCancelledByClientBody] = useState('');
    const [notifyProRdvAssignment, setNotifyProRdvAssignment] = useState(true);
    const [proRdvAssignmentSubject, setProRdvAssignmentSubject] = useState('');
    const [proRdvAssignmentBody, setProRdvAssignmentBody] = useState('');
    const [notifyExpenseApproved, setNotifyExpenseApproved] = useState(true);
    const [expenseApprovedSubject, setExpenseApprovedSubject] = useState('');
    const [expenseApprovedBody, setExpenseApprovedBody] = useState('');
    const [notifyExpenseRefused, setNotifyExpenseRefused] = useState(true);
    const [expenseRefusedSubject, setExpenseRefusedSubject] = useState('');
    const [expenseRefusedBody, setExpenseRefusedBody] = useState('');
    const [notifyKioskPinAssigned, setNotifyKioskPinAssigned] = useState(true);
    const [kioskPinAssignedSubject, setKioskPinAssignedSubject] = useState('');
    const [kioskPinAssignedBody, setKioskPinAssignedBody] = useState('');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Les trois emails du devis vivent dans un seul objet : leurs blocs portent eux-mêmes leurs
    // références et leur champ actif (voir SuiviPrivatisationNotificationBlocks), il n'y a donc rien à
    // déclarer ici en dehors des valeurs.
    const [devisNotifs, setDevisNotifs] = useState<DevisNotifications>({
        plaquette_send_subject: '', plaquette_send_body: '', plaquette_default_template_id: '',
        devis_send_subject: '', devis_send_body: '',
        notify_devis_signed_received: true, devis_signed_received_subject: '', devis_signed_received_body: '',
        notify_devis_declined: true, devis_declined_subject: '', devis_declined_body: '',
    });
    // Les trois courriels du Portail Organisateur (lot 10) : même principe, enregistrés par section (PATCH).
    const [orgaNotifs, setOrgaNotifs] = useState<OrganisateurNotifications>(ORGANISATEUR_NOTIFS_VIDES);
    const [demandeDevisNotifs, setDemandeDevisNotifs] = useState<DemandeDevisNotifications>(DEMANDE_DEVIS_NOTIFS_VIDES);
    const [factureActiveField, setFactureActiveField] = useState<'subject' | 'body'>('body');
    const [factureSendPaidActiveField, setFactureSendPaidActiveField] = useState<'subject' | 'body'>('body');
    const [congesAcceptedActiveField, setCongesAcceptedActiveField] = useState<'subject' | 'body'>('body');
    const [congesRefusedActiveField, setCongesRefusedActiveField] = useState<'subject' | 'body'>('body');
    const [evenementielActiveField, setEvenementielActiveField] = useState<'subject' | 'body'>('body');
    const factureSubjectRef = useRef<HTMLInputElement>(null);
    const factureBodyRef = useRef<HTMLTextAreaElement>(null);
    const factureSendPaidSubjectRef = useRef<HTMLInputElement>(null);
    const factureSendPaidBodyRef = useRef<HTMLTextAreaElement>(null);
    const congesAcceptedSubjectRef = useRef<HTMLInputElement>(null);
    const congesAcceptedBodyRef = useRef<HTMLTextAreaElement>(null);
    const congesRefusedSubjectRef = useRef<HTMLInputElement>(null);
    const congesRefusedBodyRef = useRef<HTMLTextAreaElement>(null);
    const evenementielSubjectRef = useRef<HTMLInputElement>(null);
    const evenementielBodyRef = useRef<HTMLTextAreaElement>(null);
    const [proRdvActiveField, setProRdvActiveField] = useState<'subject' | 'body'>('body');
    const proRdvSubjectRef = useRef<HTMLInputElement>(null);
    const proRdvBodyRef = useRef<HTMLTextAreaElement>(null);
    const [docPaymentActiveField, setDocPaymentActiveField] = useState<'subject' | 'body'>('body');
    const docPaymentSubjectRef = useRef<HTMLInputElement>(null);
    const docPaymentBodyRef = useRef<HTMLTextAreaElement>(null);
    const [proRdvReminderActiveField, setProRdvReminderActiveField] = useState<'subject' | 'body'>('body');
    const proRdvReminderSubjectRef = useRef<HTMLInputElement>(null);
    const proRdvReminderBodyRef = useRef<HTMLTextAreaElement>(null);
    const [proRdvRescheduleActiveField, setProRdvRescheduleActiveField] = useState<'subject' | 'body'>('body');
    const proRdvRescheduleSubjectRef = useRef<HTMLInputElement>(null);
    const proRdvRescheduleBodyRef = useRef<HTMLTextAreaElement>(null);
    const [proRdvCancelledByClientActiveField, setProRdvCancelledByClientActiveField] = useState<'subject' | 'body'>('body');
    const proRdvCancelledByClientSubjectRef = useRef<HTMLInputElement>(null);
    const proRdvCancelledByClientBodyRef = useRef<HTMLTextAreaElement>(null);
    const [proRdvAssignmentActiveField, setProRdvAssignmentActiveField] = useState<'subject' | 'body'>('body');
    const proRdvAssignmentSubjectRef = useRef<HTMLInputElement>(null);
    const proRdvAssignmentBodyRef = useRef<HTMLTextAreaElement>(null);
    const [expenseApprovedActiveField, setExpenseApprovedActiveField] = useState<'subject' | 'body'>('body');
    const expenseApprovedSubjectRef = useRef<HTMLInputElement>(null);
    const expenseApprovedBodyRef = useRef<HTMLTextAreaElement>(null);
    const [expenseRefusedActiveField, setExpenseRefusedActiveField] = useState<'subject' | 'body'>('body');
    const expenseRefusedSubjectRef = useRef<HTMLInputElement>(null);
    const expenseRefusedBodyRef = useRef<HTMLTextAreaElement>(null);
    const [kioskPinAssignedActiveField, setKioskPinAssignedActiveField] = useState<'subject' | 'body'>('body');
    const kioskPinAssignedSubjectRef = useRef<HTMLInputElement>(null);
    const kioskPinAssignedBodyRef = useRef<HTMLTextAreaElement>(null);

    const insertFactureToken = (token: string) => {
        if (factureActiveField === 'subject') insertNotificationToken(factureSubjectRef.current, factureSendSubject, setFactureSendSubject, token);
        else insertNotificationToken(factureBodyRef.current, factureSendBody, setFactureSendBody, token);
    };
    const insertFactureSendPaidToken = (token: string) => {
        if (factureSendPaidActiveField === 'subject') insertNotificationToken(factureSendPaidSubjectRef.current, factureSendPaidSubject, setFactureSendPaidSubject, token);
        else insertNotificationToken(factureSendPaidBodyRef.current, factureSendPaidBody, setFactureSendPaidBody, token);
    };
    const insertCongesAcceptedToken = (token: string) => {
        if (congesAcceptedActiveField === 'subject') insertNotificationToken(congesAcceptedSubjectRef.current, congesAcceptedSubject, setCongesAcceptedSubject, token);
        else insertNotificationToken(congesAcceptedBodyRef.current, congesAcceptedBody, setCongesAcceptedBody, token);
    };
    const insertCongesRefusedToken = (token: string) => {
        if (congesRefusedActiveField === 'subject') insertNotificationToken(congesRefusedSubjectRef.current, congesRefusedSubject, setCongesRefusedSubject, token);
        else insertNotificationToken(congesRefusedBodyRef.current, congesRefusedBody, setCongesRefusedBody, token);
    };
    const insertEvenementielToken = (token: string) => {
        if (evenementielActiveField === 'subject') insertNotificationToken(evenementielSubjectRef.current, evenementielUpdateSubject, setEvenementielUpdateSubject, token);
        else insertNotificationToken(evenementielBodyRef.current, evenementielUpdateBody, setEvenementielUpdateBody, token);
    };
    const insertProRdvToken = (token: string) => {
        if (proRdvActiveField === 'subject') insertNotificationToken(proRdvSubjectRef.current, proRdvConfirmationSubject, setProRdvConfirmationSubject, token);
        else insertNotificationToken(proRdvBodyRef.current, proRdvConfirmationBody, setProRdvConfirmationBody, token);
    };
    const insertDocPaymentToken = (token: string) => {
        if (docPaymentActiveField === 'subject') insertNotificationToken(docPaymentSubjectRef.current, docPaymentConfirmedSubject, setDocPaymentConfirmedSubject, token);
        else insertNotificationToken(docPaymentBodyRef.current, docPaymentConfirmedBody, setDocPaymentConfirmedBody, token);
    };
    const insertProRdvReminderToken = (token: string) => {
        if (proRdvReminderActiveField === 'subject') insertNotificationToken(proRdvReminderSubjectRef.current, proRdvReminderSubject, setProRdvReminderSubject, token);
        else insertNotificationToken(proRdvReminderBodyRef.current, proRdvReminderBody, setProRdvReminderBody, token);
    };
    const insertProRdvRescheduleToken = (token: string) => {
        if (proRdvRescheduleActiveField === 'subject') insertNotificationToken(proRdvRescheduleSubjectRef.current, proRdvRescheduleSubject, setProRdvRescheduleSubject, token);
        else insertNotificationToken(proRdvRescheduleBodyRef.current, proRdvRescheduleBody, setProRdvRescheduleBody, token);
    };
    const insertProRdvCancelledByClientToken = (token: string) => {
        if (proRdvCancelledByClientActiveField === 'subject') insertNotificationToken(proRdvCancelledByClientSubjectRef.current, proRdvCancelledByClientSubject, setProRdvCancelledByClientSubject, token);
        else insertNotificationToken(proRdvCancelledByClientBodyRef.current, proRdvCancelledByClientBody, setProRdvCancelledByClientBody, token);
    };
    const insertProRdvAssignmentToken = (token: string) => {
        if (proRdvAssignmentActiveField === 'subject') insertNotificationToken(proRdvAssignmentSubjectRef.current, proRdvAssignmentSubject, setProRdvAssignmentSubject, token);
        else insertNotificationToken(proRdvAssignmentBodyRef.current, proRdvAssignmentBody, setProRdvAssignmentBody, token);
    };
    const insertExpenseApprovedToken = (token: string) => {
        if (expenseApprovedActiveField === 'subject') insertNotificationToken(expenseApprovedSubjectRef.current, expenseApprovedSubject, setExpenseApprovedSubject, token);
        else insertNotificationToken(expenseApprovedBodyRef.current, expenseApprovedBody, setExpenseApprovedBody, token);
    };
    const insertExpenseRefusedToken = (token: string) => {
        if (expenseRefusedActiveField === 'subject') insertNotificationToken(expenseRefusedSubjectRef.current, expenseRefusedSubject, setExpenseRefusedSubject, token);
        else insertNotificationToken(expenseRefusedBodyRef.current, expenseRefusedBody, setExpenseRefusedBody, token);
    };
    const insertKioskPinAssignedToken = (token: string) => {
        if (kioskPinAssignedActiveField === 'subject') insertNotificationToken(kioskPinAssignedSubjectRef.current, kioskPinAssignedSubject, setKioskPinAssignedSubject, token);
        else insertNotificationToken(kioskPinAssignedBodyRef.current, kioskPinAssignedBody, setKioskPinAssignedBody, token);
    };

    const wrapFactureBody = (marker: string) => wrapNotificationSelection(factureBodyRef.current, factureSendBody, setFactureSendBody, marker);
    const wrapFactureSendPaidBody = (marker: string) => wrapNotificationSelection(factureSendPaidBodyRef.current, factureSendPaidBody, setFactureSendPaidBody, marker);
    const wrapCongesAcceptedBody = (marker: string) => wrapNotificationSelection(congesAcceptedBodyRef.current, congesAcceptedBody, setCongesAcceptedBody, marker);
    const wrapCongesRefusedBody = (marker: string) => wrapNotificationSelection(congesRefusedBodyRef.current, congesRefusedBody, setCongesRefusedBody, marker);
    const wrapEvenementielBody = (marker: string) => wrapNotificationSelection(evenementielBodyRef.current, evenementielUpdateBody, setEvenementielUpdateBody, marker);
    const wrapProRdvBody = (marker: string) => wrapNotificationSelection(proRdvBodyRef.current, proRdvConfirmationBody, setProRdvConfirmationBody, marker);
    const wrapDocPaymentBody = (marker: string) => wrapNotificationSelection(docPaymentBodyRef.current, docPaymentConfirmedBody, setDocPaymentConfirmedBody, marker);
    const wrapProRdvReminderBody = (marker: string) => wrapNotificationSelection(proRdvReminderBodyRef.current, proRdvReminderBody, setProRdvReminderBody, marker);
    const wrapProRdvRescheduleBody = (marker: string) => wrapNotificationSelection(proRdvRescheduleBodyRef.current, proRdvRescheduleBody, setProRdvRescheduleBody, marker);
    const wrapProRdvCancelledByClientBody = (marker: string) => wrapNotificationSelection(proRdvCancelledByClientBodyRef.current, proRdvCancelledByClientBody, setProRdvCancelledByClientBody, marker);
    const wrapProRdvAssignmentBody = (marker: string) => wrapNotificationSelection(proRdvAssignmentBodyRef.current, proRdvAssignmentBody, setProRdvAssignmentBody, marker);
    const wrapExpenseApprovedBody = (marker: string) => wrapNotificationSelection(expenseApprovedBodyRef.current, expenseApprovedBody, setExpenseApprovedBody, marker);
    const wrapExpenseRefusedBody = (marker: string) => wrapNotificationSelection(expenseRefusedBodyRef.current, expenseRefusedBody, setExpenseRefusedBody, marker);
    const wrapKioskPinAssignedBody = (marker: string) => wrapNotificationSelection(kioskPinAssignedBodyRef.current, kioskPinAssignedBody, setKioskPinAssignedBody, marker);

    useEffect(() => {
        moduleApi.getClientNotificationSettings().then((s: any) => {
            setNotifyFactureSend(s?.notify_facture_send !== false);
            setNotifyFactureSendPaid(s?.notify_facture_send_paid !== false);
            setNotifyProRdvReminder(s?.notify_pro_rdv_reminder !== false);
            setNotifyProRdvReschedule(s?.notify_pro_rdv_reschedule !== false);
            setFactureSendSubject(s?.facture_send_subject || '');
            setFactureSendBody(s?.facture_send_body || '');
            setFactureSendPaidSubject(s?.facture_send_paid_subject || '');
            setFactureSendPaidBody(s?.facture_send_paid_body || '');
            setNotifyCongesAccepted(s?.notify_conges_accepted !== false);
            setCongesAcceptedSubject(s?.conges_accepted_subject || '');
            setCongesAcceptedBody(s?.conges_accepted_body || '');
            setNotifyCongesRefused(s?.notify_conges_refused !== false);
            setCongesRefusedSubject(s?.conges_refused_subject || '');
            setCongesRefusedBody(s?.conges_refused_body || '');
            setNotifyEvenementielUpdate(s?.notify_evenementiel_update !== false);
            setEvenementielUpdateSubject(s?.evenementiel_update_subject || '');
            setEvenementielUpdateBody(s?.evenementiel_update_body || '');
            setNotifyProRdvConfirmation(s?.notify_pro_rdv_confirmation !== false);
            setProRdvConfirmationSubject(s?.pro_rdv_confirmation_subject || '');
            setProRdvConfirmationBody(s?.pro_rdv_confirmation_body || '');
            setNotifyDocPaymentConfirmed(s?.notify_doc_payment_confirmed !== false);
            setDocPaymentConfirmedSubject(s?.doc_payment_confirmed_subject || '');
            setDocPaymentConfirmedBody(s?.doc_payment_confirmed_body || '');
            setProRdvReminderSubject(s?.pro_rdv_reminder_subject || '');
            setProRdvReminderBody(s?.pro_rdv_reminder_body || '');
            setProRdvRescheduleSubject(s?.pro_rdv_reschedule_subject || '');
            setProRdvRescheduleBody(s?.pro_rdv_reschedule_body || '');
            setNotifyProRdvCancelledByClient(s?.notify_pro_rdv_cancelled_by_client !== false);
            setProRdvCancelledByClientSubject(s?.pro_rdv_cancelled_by_client_subject || '');
            setProRdvCancelledByClientBody(s?.pro_rdv_cancelled_by_client_body || '');
            setNotifyProRdvAssignment(s?.notify_pro_rdv_assignment !== false);
            setProRdvAssignmentSubject(s?.pro_rdv_assignment_subject || '');
            setProRdvAssignmentBody(s?.pro_rdv_assignment_body || '');
            setNotifyExpenseApproved(s?.notify_expense_approved !== false);
            setExpenseApprovedSubject(s?.expense_approved_subject || '');
            setExpenseApprovedBody(s?.expense_approved_body || '');
            setNotifyExpenseRefused(s?.notify_expense_refused !== false);
            setExpenseRefusedSubject(s?.expense_refused_subject || '');
            setExpenseRefusedBody(s?.expense_refused_body || '');
            setNotifyKioskPinAssigned(s?.notify_kiosk_pin_assigned !== false);
            setKioskPinAssignedSubject(s?.kiosk_pin_assigned_subject || '');
            setKioskPinAssignedBody(s?.kiosk_pin_assigned_body || '');
            setDevisNotifs({
                plaquette_send_subject: s?.plaquette_send_subject || '',
                plaquette_send_body: s?.plaquette_send_body || '',
                plaquette_default_template_id: s?.plaquette_default_template_id || '',
                devis_send_subject: s?.devis_send_subject || '',
                devis_send_body: s?.devis_send_body || '',
                notify_devis_signed_received: s?.notify_devis_signed_received !== false,
                devis_signed_received_subject: s?.devis_signed_received_subject || '',
                devis_signed_received_body: s?.devis_signed_received_body || '',
                notify_devis_declined: s?.notify_devis_declined !== false,
                devis_declined_subject: s?.devis_declined_subject || '',
                devis_declined_body: s?.devis_declined_body || '',
            });
            setDemandeDevisNotifs({
                notify_devis_request_new: s?.notify_devis_request_new !== false,
                devis_request_new_subject: s?.devis_request_new_subject || '',
                devis_request_new_body: s?.devis_request_new_body || '',
                notify_devis_request_ack: s?.notify_devis_request_ack !== false,
                devis_request_ack_subject: s?.devis_request_ack_subject || '',
                devis_request_ack_body: s?.devis_request_ack_body || '',
            });
            setOrgaNotifs({
                organisateur_acces_subject: s?.organisateur_acces_subject || '',
                organisateur_acces_body: s?.organisateur_acces_body || '',
                notify_organisateur_envoi: s?.notify_organisateur_envoi !== false,
                organisateur_envoi_subject: s?.organisateur_envoi_subject || '',
                organisateur_envoi_body: s?.organisateur_envoi_body || '',
                notify_organisateur_absence: s?.notify_organisateur_absence !== false,
                organisateur_absence_subject: s?.organisateur_absence_subject || '',
                organisateur_absence_body: s?.organisateur_absence_body || '',
            });
        }).catch((e) => console.error('Failed to load client notification settings', e)).finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setEchecEnregistrement(false);
        try {
            const result: any = await moduleApi.saveClientNotificationSettings({
                ...devisNotifs,
                ...demandeDevisNotifs,
                notify_facture_send: notifyFactureSend, facture_send_subject: factureSendSubject, facture_send_body: factureSendBody,
                notify_facture_send_paid: notifyFactureSendPaid,
                notify_pro_rdv_reminder: notifyProRdvReminder, notify_pro_rdv_reschedule: notifyProRdvReschedule,
                facture_send_paid_subject: factureSendPaidSubject, facture_send_paid_body: factureSendPaidBody,
                notify_conges_accepted: notifyCongesAccepted, conges_accepted_subject: congesAcceptedSubject, conges_accepted_body: congesAcceptedBody,
                notify_conges_refused: notifyCongesRefused, conges_refused_subject: congesRefusedSubject, conges_refused_body: congesRefusedBody,
                notify_evenementiel_update: notifyEvenementielUpdate, evenementiel_update_subject: evenementielUpdateSubject, evenementiel_update_body: evenementielUpdateBody,
                notify_pro_rdv_confirmation: notifyProRdvConfirmation, pro_rdv_confirmation_subject: proRdvConfirmationSubject, pro_rdv_confirmation_body: proRdvConfirmationBody,
                notify_doc_payment_confirmed: notifyDocPaymentConfirmed, doc_payment_confirmed_subject: docPaymentConfirmedSubject, doc_payment_confirmed_body: docPaymentConfirmedBody,
                pro_rdv_reminder_subject: proRdvReminderSubject, pro_rdv_reminder_body: proRdvReminderBody,
                pro_rdv_reschedule_subject: proRdvRescheduleSubject, pro_rdv_reschedule_body: proRdvRescheduleBody,
                notify_pro_rdv_cancelled_by_client: notifyProRdvCancelledByClient, pro_rdv_cancelled_by_client_subject: proRdvCancelledByClientSubject, pro_rdv_cancelled_by_client_body: proRdvCancelledByClientBody,
                notify_pro_rdv_assignment: notifyProRdvAssignment, pro_rdv_assignment_subject: proRdvAssignmentSubject, pro_rdv_assignment_body: proRdvAssignmentBody,
                notify_expense_approved: notifyExpenseApproved, expense_approved_subject: expenseApprovedSubject, expense_approved_body: expenseApprovedBody,
                notify_expense_refused: notifyExpenseRefused, expense_refused_subject: expenseRefusedSubject, expense_refused_body: expenseRefusedBody,
                notify_kiosk_pin_assigned: notifyKioskPinAssigned, kiosk_pin_assigned_subject: kioskPinAssignedSubject, kiosk_pin_assigned_body: kioskPinAssignedBody,
            });
            if (showOrganisateur) {
                const o: any = await moduleApi.patchClientNotificationSettings('organisateur', { ...orgaNotifs });
                if (o) setOrgaNotifs((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(o).filter(([k]) => k in prev)) }));
            }
            if (result) {
                setFactureSendSubject(result.facture_send_subject || '');
                setFactureSendBody(result.facture_send_body || '');
                setFactureSendPaidSubject(result.facture_send_paid_subject || '');
                setFactureSendPaidBody(result.facture_send_paid_body || '');
                setCongesAcceptedSubject(result.conges_accepted_subject || '');
                setCongesAcceptedBody(result.conges_accepted_body || '');
                setCongesRefusedSubject(result.conges_refused_subject || '');
                setCongesRefusedBody(result.conges_refused_body || '');
                setEvenementielUpdateSubject(result.evenementiel_update_subject || '');
                setEvenementielUpdateBody(result.evenementiel_update_body || '');
                setProRdvConfirmationSubject(result.pro_rdv_confirmation_subject || '');
                setProRdvConfirmationBody(result.pro_rdv_confirmation_body || '');
                setDocPaymentConfirmedSubject(result.doc_payment_confirmed_subject || '');
                setDocPaymentConfirmedBody(result.doc_payment_confirmed_body || '');
                setProRdvReminderSubject(result.pro_rdv_reminder_subject || '');
                setProRdvReminderBody(result.pro_rdv_reminder_body || '');
                setProRdvRescheduleSubject(result.pro_rdv_reschedule_subject || '');
                setProRdvRescheduleBody(result.pro_rdv_reschedule_body || '');
                setProRdvCancelledByClientSubject(result.pro_rdv_cancelled_by_client_subject || '');
                setProRdvCancelledByClientBody(result.pro_rdv_cancelled_by_client_body || '');
                setProRdvAssignmentSubject(result.pro_rdv_assignment_subject || '');
                setProRdvAssignmentBody(result.pro_rdv_assignment_body || '');
                setExpenseApprovedSubject(result.expense_approved_subject || '');
                setExpenseApprovedBody(result.expense_approved_body || '');
                setExpenseRefusedSubject(result.expense_refused_subject || '');
                setExpenseRefusedBody(result.expense_refused_body || '');
                setKioskPinAssignedSubject(result.kiosk_pin_assigned_subject || '');
                setKioskPinAssignedBody(result.kiosk_pin_assigned_body || '');
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save client notification settings', e);
            setEchecEnregistrement(true);
        } finally {
            setSaving(false);
        }
    };

    if (!hasClientContent && !hasSalarieContent) return null;

    const tabButton = (active: boolean) =>
        `flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${active ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`;

    const saveButton = (
        <button onClick={handleSave} disabled={saving}
            className="px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold shadow-lg disabled:opacity-50">
            {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
        </button>
    );

    const contenuClient = (
        <>
            {showFacture && (
                <GroupeNotifications titre="Factures" icone={<Receipt size={12} />}>
                    <NotificationBlock title="Envoi d'une facture avec solde à payer"
                        description="Part quand vous cliquez sur « Envoyer par email » depuis une facture dont il reste un montant à régler."
                        toggle={{ checked: notifyFactureSend, onChange: setNotifyFactureSend }}
                        subject={factureSendSubject} setSubject={setFactureSendSubject} body={factureSendBody} setBody={setFactureSendBody}
                        subjectRef={factureSubjectRef} bodyRef={factureBodyRef}
                        activeField={factureActiveField} setActiveField={setFactureActiveField}
                        onInsertToken={insertFactureToken} tokens={ALL_CLIENT_TOKENS} />
                    <NotificationBlock title="Envoi d'une facture déjà réglée"
                        description="Part quand vous envoyez une facture déjà payée en entier (pas de mention de paiement en ligne)."
                        toggle={{ checked: notifyFactureSendPaid, onChange: setNotifyFactureSendPaid }}
                        subject={factureSendPaidSubject} setSubject={setFactureSendPaidSubject} body={factureSendPaidBody} setBody={setFactureSendPaidBody}
                        subjectRef={factureSendPaidSubjectRef} bodyRef={factureSendPaidBodyRef}
                        activeField={factureSendPaidActiveField} setActiveField={setFactureSendPaidActiveField}
                        onInsertToken={insertFactureSendPaidToken} tokens={ALL_CLIENT_TOKENS} />
                </GroupeNotifications>
            )}
            {showProRdv && (
                <GroupeNotifications titre="RDV Pro" icone={<CalendarCheck size={12} />}>
                    <NotificationBlock title="Confirmation de rendez-vous"
                        description="Part au contact quand il prend rendez-vous depuis une Démarche client."
                        toggle={{ checked: notifyProRdvConfirmation, onChange: setNotifyProRdvConfirmation }}
                        subject={proRdvConfirmationSubject} setSubject={setProRdvConfirmationSubject} body={proRdvConfirmationBody} setBody={setProRdvConfirmationBody}
                        subjectRef={proRdvSubjectRef} bodyRef={proRdvBodyRef}
                        activeField={proRdvActiveField} setActiveField={setProRdvActiveField}
                        onInsertToken={insertProRdvToken} tokens={RDV_PRO_TOKENS} />
                    <NotificationBlock title="Rappel de rendez-vous"
                        description="Part quand vous cliquez sur « Rappeler le RDV »."
                        toggle={{ checked: notifyProRdvReminder, onChange: setNotifyProRdvReminder }}
                        subject={proRdvReminderSubject} setSubject={setProRdvReminderSubject} body={proRdvReminderBody} setBody={setProRdvReminderBody}
                        subjectRef={proRdvReminderSubjectRef} bodyRef={proRdvReminderBodyRef}
                        activeField={proRdvReminderActiveField} setActiveField={setProRdvReminderActiveField}
                        onInsertToken={insertProRdvReminderToken} tokens={RDV_PRO_TOKENS} />
                    <NotificationBlock title="Rendez-vous déplacé"
                        description="Part quand vous modifiez un rendez-vous en cochant la case pour prévenir le client."
                        toggle={{ checked: notifyProRdvReschedule, onChange: setNotifyProRdvReschedule }}
                        subject={proRdvRescheduleSubject} setSubject={setProRdvRescheduleSubject} body={proRdvRescheduleBody} setBody={setProRdvRescheduleBody}
                        subjectRef={proRdvRescheduleSubjectRef} bodyRef={proRdvRescheduleBodyRef}
                        activeField={proRdvRescheduleActiveField} setActiveField={setProRdvRescheduleActiveField}
                        onInsertToken={insertProRdvRescheduleToken} tokens={RDV_PRO_TOKENS} />
                    <NotificationBlock title="Annulation par le client (pour vous)"
                        description="Vous prévient quand le client clique « Annuler le RDV » dans son mail de confirmation et indique un motif."
                        toggle={{ checked: notifyProRdvCancelledByClient, onChange: setNotifyProRdvCancelledByClient }}
                        subject={proRdvCancelledByClientSubject} setSubject={setProRdvCancelledByClientSubject} body={proRdvCancelledByClientBody} setBody={setProRdvCancelledByClientBody}
                        subjectRef={proRdvCancelledByClientSubjectRef} bodyRef={proRdvCancelledByClientBodyRef}
                        activeField={proRdvCancelledByClientActiveField} setActiveField={setProRdvCancelledByClientActiveField}
                        onInsertToken={insertProRdvCancelledByClientToken} tokens={PRO_RDV_CANCELLED_TOKENS} />
                </GroupeNotifications>
            )}
            {showSuiviPrivatisation && (
                <SuiviPrivatisationNotificationBlocks
                    valeurs={devisNotifs}
                    onChange={(patch) => setDevisNotifs((prev) => ({ ...prev, ...patch }))} />
            )}
            {showDemandeDevis && (
                <DemandeDevisNotificationBlocks
                    valeurs={demandeDevisNotifs}
                    onChange={(patch) => setDemandeDevisNotifs((prev) => ({ ...prev, ...patch }))} />
            )}
            {showOrganisateur && (
                <OrganisateurNotificationBlocks
                    valeurs={orgaNotifs}
                    onChange={(patch) => setOrgaNotifs((prev) => ({ ...prev, ...patch }))} />
            )}
            {showDocPayment && (
                <GroupeNotifications titre="Démarches client" icone={<FileText size={12} />}>
                    <NotificationBlock title="Confirmation de commande"
                        description="Part au contact une fois son paiement confirmé (bouton « Marquer comme payé »)."
                        toggle={{ checked: notifyDocPaymentConfirmed, onChange: setNotifyDocPaymentConfirmed }}
                        subject={docPaymentConfirmedSubject} setSubject={setDocPaymentConfirmedSubject} body={docPaymentConfirmedBody} setBody={setDocPaymentConfirmedBody}
                        subjectRef={docPaymentSubjectRef} bodyRef={docPaymentBodyRef}
                        activeField={docPaymentActiveField} setActiveField={setDocPaymentActiveField}
                        onInsertToken={insertDocPaymentToken} tokens={DOC_PAYMENT_TOKENS} />
                </GroupeNotifications>
            )}
        </>
    );

    const contenuSalarie = (
        <>
            {showConges && (
                <GroupeNotifications titre="Demande de congé(s)" icone={<CalendarClock size={12} />}>
                    <NotificationBlock title="Absence acceptée"
                        description="Part au salarié quand sa demande d'absence est acceptée."
                        toggle={{ checked: notifyCongesAccepted, onChange: setNotifyCongesAccepted }}
                        subject={congesAcceptedSubject} setSubject={setCongesAcceptedSubject} body={congesAcceptedBody} setBody={setCongesAcceptedBody}
                        subjectRef={congesAcceptedSubjectRef} bodyRef={congesAcceptedBodyRef}
                        activeField={congesAcceptedActiveField} setActiveField={setCongesAcceptedActiveField}
                        onInsertToken={insertCongesAcceptedToken} tokens={ALL_CLIENT_TOKENS} />
                    <NotificationBlock title="Absence refusée"
                        description="Part au salarié quand sa demande d'absence est refusée."
                        toggle={{ checked: notifyCongesRefused, onChange: setNotifyCongesRefused }}
                        subject={congesRefusedSubject} setSubject={setCongesRefusedSubject} body={congesRefusedBody} setBody={setCongesRefusedBody}
                        subjectRef={congesRefusedSubjectRef} bodyRef={congesRefusedBodyRef}
                        activeField={congesRefusedActiveField} setActiveField={setCongesRefusedActiveField}
                        onInsertToken={insertCongesRefusedToken} tokens={ALL_CLIENT_TOKENS} />
                </GroupeNotifications>
            )}
            {showEvenementiel && (
                <GroupeNotifications titre="Événementiel" icone={<PartyPopper size={12} />}>
                    <NotificationBlock title="Mise à jour du planning"
                        description="Part au personnel concerné quand vous mettez à jour le planning des événements."
                        toggle={{ checked: notifyEvenementielUpdate, onChange: setNotifyEvenementielUpdate }}
                        subject={evenementielUpdateSubject} setSubject={setEvenementielUpdateSubject} body={evenementielUpdateBody} setBody={setEvenementielUpdateBody}
                        subjectRef={evenementielSubjectRef} bodyRef={evenementielBodyRef}
                        activeField={evenementielActiveField} setActiveField={setEvenementielActiveField}
                        onInsertToken={insertEvenementielToken} tokens={ALL_CLIENT_TOKENS} />
                </GroupeNotifications>
            )}
            {showProRdvAssignment && (
                <GroupeNotifications titre="RDV Pro" icone={<CalendarCheck size={12} />}>
                    <NotificationBlock title="Assignation à un rendez-vous"
                        description="Part au salarié dès qu'il est assigné à un rendez-vous."
                        toggle={{ checked: notifyProRdvAssignment, onChange: setNotifyProRdvAssignment }}
                        subject={proRdvAssignmentSubject} setSubject={setProRdvAssignmentSubject} body={proRdvAssignmentBody} setBody={setProRdvAssignmentBody}
                        subjectRef={proRdvAssignmentSubjectRef} bodyRef={proRdvAssignmentBodyRef}
                        activeField={proRdvAssignmentActiveField} setActiveField={setProRdvAssignmentActiveField}
                        onInsertToken={insertProRdvAssignmentToken} tokens={PRO_RDV_ASSIGNMENT_TOKENS} />
                </GroupeNotifications>
            )}
            {showExpense && (
                <GroupeNotifications titre="Notes de frais" icone={<Wallet size={12} />}>
                    <NotificationBlock title="Note de frais approuvée"
                        description="Part au salarié quand sa note de frais est approuvée."
                        toggle={{ checked: notifyExpenseApproved, onChange: setNotifyExpenseApproved }}
                        subject={expenseApprovedSubject} setSubject={setExpenseApprovedSubject} body={expenseApprovedBody} setBody={setExpenseApprovedBody}
                        subjectRef={expenseApprovedSubjectRef} bodyRef={expenseApprovedBodyRef}
                        activeField={expenseApprovedActiveField} setActiveField={setExpenseApprovedActiveField}
                        onInsertToken={insertExpenseApprovedToken} tokens={EXPENSE_TOKENS} />
                    <NotificationBlock title="Note de frais refusée"
                        description="Part au salarié quand sa note de frais est refusée."
                        toggle={{ checked: notifyExpenseRefused, onChange: setNotifyExpenseRefused }}
                        subject={expenseRefusedSubject} setSubject={setExpenseRefusedSubject} body={expenseRefusedBody} setBody={setExpenseRefusedBody}
                        subjectRef={expenseRefusedSubjectRef} bodyRef={expenseRefusedBodyRef}
                        activeField={expenseRefusedActiveField} setActiveField={setExpenseRefusedActiveField}
                        onInsertToken={insertExpenseRefusedToken} tokens={EXPENSE_TOKENS} />
                </GroupeNotifications>
            )}
            {showKioskPin && (
                <GroupeNotifications titre="Mode kiosque (pointage)" icone={<Wallet size={12} />}>
                    <NotificationBlock title="Code PIN du kiosque"
                        description="Part au salarié à l'activation du kiosque ou quand vous renvoyez son code (Postes & Employés > Pointage)."
                        toggle={{ checked: notifyKioskPinAssigned, onChange: setNotifyKioskPinAssigned }}
                        subject={kioskPinAssignedSubject} setSubject={setKioskPinAssignedSubject} body={kioskPinAssignedBody} setBody={setKioskPinAssignedBody}
                        subjectRef={kioskPinAssignedSubjectRef} bodyRef={kioskPinAssignedBodyRef}
                        activeField={kioskPinAssignedActiveField} setActiveField={setKioskPinAssignedActiveField}
                        onInsertToken={insertKioskPinAssignedToken} tokens={KIOSK_PIN_TOKENS} />
                </GroupeNotifications>
            )}
        </>
    );

    // Paramètres > Notifications : le filtre de destinataire et l'enregistrement sont portés par la page
    // et par chaque ligne ; cet écran ne montre que ses emails.
    if (enLignes) {
        if (recipient === 'etablissement') return null;
        const voirClient = recipient === 'client' && hasClientContent;
        const voirSalarie = recipient === 'salarie' && hasSalarieContent;
        if (!voirClient && !voirSalarie) return null;
        return (
            <FournisseurEnregistrement enregistrer={handleSave}>
                {loading
                    ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
                    : <div className="space-y-6">{voirClient ? contenuClient : contenuSalarie}</div>}
                <EtatEnregistrement etat={saving ? 'en_cours' : echecEnregistrement ? 'echec' : saved ? 'fait' : 'repos'} />
            </FournisseurEnregistrement>
        );
    }

    return (
        <div className="space-y-6">
            {hasClientContent && hasSalarieContent && !destinataire && (
                <div className="bg-surface border border-border rounded-2xl p-2 inline-flex gap-2">
                    <button onClick={() => setRecipient('client')} className={tabButton(recipient === 'client')}>
                        <User size={14} /> Client
                    </button>
                    <button onClick={() => setRecipient('salarie')} className={tabButton(recipient === 'salarie')}>
                        <UsersIcon size={14} /> Salarié
                    </button>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : (
                <div className="max-w-3xl space-y-6">
                    {(recipient === 'client' && hasClientContent) ? contenuClient : contenuSalarie}
                    <div className="pt-2">{saveButton}</div>
                </div>
            )}
        </div>
    );
};
